<template>
    <sliding-sidebar
        v-model="isOpened"
        title="Operations"
        @on-escape-key-up="close"
        @sidebar-opened="onOpen"
    >
        <venue-selector
            :venues="venues"
            :default-venue-select="defaultVenue"
            @selected="onVenueSelected"
        />

        <div v-if="venue" class="relative py-4">
            <div
                v-if="isLoadingVenue"
                class="absolute inset-0 z-10 flex items-center justify-center rounded-lg bg-white bg-opacity-75"
            >
                <base-spinner :is-loading="true" />
            </div>

            <div class="mb-6">
                <h3 class="mb-2 font-semibold leading-6 text-gray-500"
                    >Auto Accept
                </h3>

                <div class="flex items-center">
                    <span
                        role="checkbox"
                        tabindex="0"
                        aria-checked="false"
                        class="group relative inline-flex h-5 w-10 flex-shrink-0 cursor-pointer items-center justify-center focus:outline-none"
                        @click="toggleAutoAccept"
                    >
                        <span
                            aria-hidden="true"
                            :class="{
                                'bg-gray-200': !isAutoAccept,
                                'bg-indigo-600': isAutoAccept
                            }"
                            class="absolute mx-auto h-4 w-9 rounded-full bg-gray-200 transition-colors duration-200 ease-in-out"
                        ></span>
                        <span
                            aria-hidden="true"
                            :class="{
                                'translate-x-0': !isAutoAccept,
                                'translate-x-5': isAutoAccept
                            }"
                            class="absolute left-0 inline-block h-5 w-5 translate-x-0 transform rounded-full border border-gray-200 bg-white shadow transition-transform duration-200 ease-in-out group-focus:border-blue-300 group-focus:shadow-outline"
                        ></span>
                    </span>
                    <span
                        class="pl-3 text-sm font-medium leading-5 text-gray-700"
                    >
                        Auto Accept
                    </span>
                </div>
            </div>
            <snooze-options
                title="Snooze Orders"
                :selected-option="selectedOption"
                :options="options"
                @option-selected="selectOption"
            />

            <div>
                <h3 class="mb-2 font-semibold leading-6 text-gray-500"
                    >Preparation time
                </h3>
                <div class="grid grid-cols-6 gap-6">
                    <div class="col-span-6 sm:col-span-6 lg:col-span-3">
                        <span class="text-sm leading-5 text-gray-500">{{
                            isOrderAndPay
                                ? 'Average prep time'
                                : 'Delivery orders'
                        }}</span
                        ><div class="relative mt-1 w-full rounded-md shadow-sm">
                            <currency-input
                                id="prepTime"
                                v-model="deliveryOrdersTime"
                                class="form-input block w-full pr-12 sm:text-sm sm:leading-5"
                                :class="{
                                    'border-red-300':
                                        $v.deliveryOrdersTime.$error
                                }"
                                placeholder="30"
                                :currency="null"
                                :precision="0"
                                locale="en-GB"
                                :value-as-integer="true"
                                :allow-negative="false"
                            ></currency-input>
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span
                                    class="text-gray-500 sm:text-sm sm:leading-5"
                                >
                                    Minutes
                                </span>
                            </div>
                        </div>
                    </div>
                    <div
                        v-if="!isOrderAndPay"
                        class="col-span-6 sm:col-span-6 lg:col-span-3"
                    >
                        <span class="text-sm leading-5 text-gray-500"
                            >Pickup orders</span
                        ><div class="relative mt-1 w-full rounded-md shadow-sm">
                            <currency-input
                                id="collectionPrepTime"
                                v-model="pickupOrdersTime"
                                class="form-input block w-full pr-12 sm:text-sm sm:leading-5"
                                :class="{
                                    'border-red-300': $v.pickupOrdersTime.$error
                                }"
                                placeholder="30"
                                :currency="null"
                                :precision="0"
                                locale="en-GB"
                                :value-as-integer="true"
                                :allow-negative="false"
                            ></currency-input>
                            <div
                                class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3"
                            >
                                <span
                                    class="text-gray-500 sm:text-sm sm:leading-5"
                                >
                                    Minutes
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div
                class="mt-5 justify-between sm:mt-6 sm:flex sm:w-auto"
                :class="{ spinner: isLoading }"
            >
                <span class="flex rounded-md shadow-sm">
                    <button
                        type="button"
                        class="inline-flex w-full justify-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-base font-medium leading-6 text-white shadow-sm transition duration-150 ease-in-out hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none sm:text-sm sm:leading-5"
                        :disabled="isLoading"
                        @click="save"
                    >
                        Save changes
                    </button>
                </span>
                <span
                    class="mt-3 flex w-full rounded-md shadow-sm sm:ml-3 sm:mt-0 sm:w-auto"
                >
                    <button
                        class="inline-flex w-full justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium leading-6 text-gray-700 shadow-sm transition duration-150 ease-in-out hover:text-gray-500 focus:border-blue-300 focus:shadow-outline focus:outline-none sm:text-sm sm:leading-5"
                        @click="close"
                    >
                        Close
                    </button>
                </span>
            </div>
        </div>
    </sliding-sidebar>
</template>

<script>
import { mapActions } from 'vuex';
import { required } from 'vuelidate/lib/validators';

import SnoozeOptions from '@/components/SnoozeOptions';
import SlidingSidebar from '@/components/SlidingSidebar';
import VenueSelector from '@/components/selectors/VenueSelector';

export default {
    name: 'LiveOrdersOperationsSidebar',
    components: { SlidingSidebar, VenueSelector, SnoozeOptions },
    props: {
        value: {
            type: Boolean,
            default: false
        },
        venues: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            venue: null,
            options: [
                { label: '5 mins', value: 5, active: true },
                { label: '10 mins', value: 10, active: true },
                { label: '15 mins', value: 15, active: true },
                { label: '30 mins', value: 30, active: true }
            ],
            selectedOption: null,
            isAutoAccept: false,
            defaultVenue: null,
            deliveryOrdersTime: null,
            pickupOrdersTime: null,
            isLoading: false,
            isLoadingVenue: false
        };
    },
    validations() {
        const validations = {
            deliveryOrdersTime: { required }
        };

        if (!this.isOrderAndPay) {
            validations.pickupOrdersTime = { required };
        }

        return validations;
    },
    computed: {
        isOpened: {
            get() {
                return this.value;
            },
            set(value) {
                this.$emit('input', value);
            }
        },
        isOrderAndPay() {
            return this.venue.acceptsInStore;
        }
    },
    methods: {
        ...mapActions({
            updateVenueSettings: 'venues/update'
        }),
        async onOpen() {
            this.defaultVenue = this.venues[0].id;
            await this.fetchVenueSettings(this.venues[0].slug);
        },
        close() {
            this.isOpened = false;
        },
        async onVenueSelected(venueId) {
            const venue =
                this.venues.find(venue => venue.id === venueId) || null;

            if (!venue) {
                return;
            }

            this.selectedOption = null;
            await this.fetchVenueSettings(venue.slug);
        },
        async fetchVenueSettings(slug) {
            try {
                this.isLoadingVenue = true;

                const { data } = await this.$axios.get(
                    `/venues/${slug}/private`
                );

                this.venue = data.venue;
                this.isAutoAccept = this.venue.autoAccept;
                this.deliveryOrdersTime = this.venue.prepTime;
                this.pickupOrdersTime = this.venue.collectionPrepTime;
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoadingVenue = false;
            }
        },
        selectOption(option) {
            this.selectedOption = option;
        },
        toggleAutoAccept() {
            this.isAutoAccept = !this.isAutoAccept;
        },
        async save() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return false;
            }

            try {
                this.isLoading = true;

                const payload = {
                    id: this.venue.id,
                    autoAccept: this.isAutoAccept,
                    prepTime: this.deliveryOrdersTime,
                    collectionPrepTime: this.pickupOrdersTime,
                    disabledForInMinutes: this.selectedOption
                };

                await this.updateVenueSettings(payload);

                this.$notify({
                    group: 'settings',
                    type: 'success',
                    duration: 3000,
                    text: 'Venue settings updated successfully'
                });
            } catch (error) {
                throw new Error(`API ${error}`);
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
